import * as Ably from 'ably';

import { fetchAblyTokenRequest } from '@lib/realtime/ably-token';

/**
 * A finished run announces itself under this name. Part of the backend's client
 * contract rather than an internal detail, so it is matched exactly.
 */
const RUN_FINISHED_MESSAGE = 'transfers.completed';

/**
 * How long to give the subscription before deciding the member is better served
 * by asking. Attaching involves minting a token and opening a socket, and the
 * first collection is not due until the backend's retry interval anyway.
 */
const ATTACH_TIMEOUT_MS = 5000;

/**
 * How far back the channel replays on attach.
 *
 * The backend starts solving the moment it accepts the run, and attaching here
 * costs a token mint and a socket open first, so a fast solve can announce
 * itself before anything is listening. Without replay that announcement is
 * lost and the member waits out the full collection patience for an answer
 * that was ready in seconds (FFH-2526).
 *
 * A duration rather than a message count, so one run's announcement cannot
 * shadow another's when two runs race. Two minutes is Ably's whole replay
 * window for a channel without persisted history, and also longer than any
 * run this collects: nothing older can still be waited on. Replayed
 * announcements for other runs are harmless, because a wait only ever
 * consumes the announcement bearing its own run id.
 */
const REPLAY_WINDOW = '120s';

/** The doorbell carries the run's identity and how it ended, never the answer. */
type RunFinishedNotification = {
  runId: string;
  outcome: string;
};

export type TransferRunDoorbell = {
  /**
   * Resolves true when the named run has been announced as finished, false when
   * the wait ran out or was abandoned. False is not a failure: it means ask.
   */
  waitForRun: (
    runId: string,
    timeoutMs: number,
    signal?: AbortSignal
  ) => Promise<boolean>;
  close: () => void;
};

const toErrorMessage = (error: unknown): string =>
  error instanceof Error ? error.message : String(error);

/**
 * A client authenticated for this member's own notification channel.
 *
 * Deliberately not the live-match singleton in `lib/live/connection`, even
 * though both now mint from the same endpoint: that client's lifetime is tied
 * to the matches on screen, and a run outlives them. Sharing it would close the
 * doorbell when the last match tile unmounts.
 */
const createClient = (accessToken: string): Ably.Realtime =>
  new Ably.Realtime({
    authCallback: async (_tokenParams, callback) => {
      try {
        callback(null, await fetchAblyTokenRequest(accessToken));
      } catch (error) {
        callback(toErrorMessage(error), null);
      }
    }
  });

const withTimeout = <T>(
  work: Promise<T>,
  timeoutMs: number,
  onTimeout: T
): Promise<T> =>
  new Promise((resolve) => {
    const timer = setTimeout(() => resolve(onTimeout), timeoutMs);
    work
      .then((value) => {
        clearTimeout(timer);
        resolve(value);
      })
      .catch(() => {
        clearTimeout(timer);
        resolve(onTimeout);
      });
  });

/**
 * Listens for completion on the channel a run named, or reports that it cannot.
 *
 * Returns undefined whenever listening is not possible: no channel, no token,
 * an attach that did not complete. Every one of those is a reason to ask
 * instead, never a reason to fail, because the answer is reachable by asking
 * whatever happens here.
 */
export const openTransferRunDoorbell = async ({
  channel,
  accessToken
}: {
  channel: string;
  accessToken: string;
}): Promise<TransferRunDoorbell | undefined> => {
  let client: Ably.Realtime;
  try {
    client = createClient(accessToken);
  } catch (error) {
    console.warn('[transfers] realtime client unavailable', {
      error: toErrorMessage(error)
    });
    return undefined;
  }

  // Runs announced while nothing was waiting on them. The loop that uses this
  // subscribes once and then spends most of its time in a request, so a message
  // arriving between waits must still be there when the next wait begins.
  const announced = new Set<string>();
  const waiters = new Map<string, () => void>();

  const onMessage = (message: Ably.Message) => {
    const notification = message.data as RunFinishedNotification | undefined;
    if (!notification?.runId) return;

    // A wait already in progress consumes the announcement itself, so it is
    // never recorded. Recording it as well would leave it behind for the next
    // wait to find, which is the one-shot leak the delete in waitForRun exists
    // to prevent, and this is the path an announcement normally arrives on:
    // the loop spends most of a run waiting rather than between waits.
    const waiter = waiters.get(notification.runId);
    if (waiter) {
      waiter();
      return;
    }

    announced.add(notification.runId);
  };

  const close = () => {
    try {
      client.close();
    } catch (error) {
      console.warn('[transfers] realtime close failed', {
        error: toErrorMessage(error)
      });
    }
  };

  const realtimeChannel = client.channels.get(channel, {
    params: { rewind: REPLAY_WINDOW }
  });
  const hasAttached = await withTimeout(
    realtimeChannel
      .subscribe(RUN_FINISHED_MESSAGE, onMessage)
      .then(() => true)
      .catch(() => false),
    ATTACH_TIMEOUT_MS,
    false
  );

  if (!hasAttached) {
    console.warn('[transfers] could not attach to the run channel; polling');
    close();
    return undefined;
  }

  return {
    waitForRun: (runId, timeoutMs, signal) =>
      new Promise<boolean>((resolve) => {
        // An announcement is a one-shot event, so observing it consumes it.
        // Left in place it would answer every later wait instantly, and a
        // caller that asks again because the run still reported itself as
        // working would then be told "finished" over and over with nothing to
        // wait for in between, turning the collection loop into an undelayed
        // request loop that only the deadline ends. Replay on attach makes an
        // announcement more likely to be sitting here, so this stops being
        // theoretical (FFH-2526).
        if (announced.delete(runId)) {
          resolve(true);
          return;
        }

        if (signal?.aborted) {
          resolve(false);
          return;
        }

        const settle = (hasRung: boolean) => {
          clearTimeout(timer);
          waiters.delete(runId);
          signal?.removeEventListener('abort', onAbort);
          resolve(hasRung);
        };
        const timer = setTimeout(() => settle(false), timeoutMs);
        const onAbort = () => settle(false);

        waiters.set(runId, () => settle(true));
        signal?.addEventListener('abort', onAbort, { once: true });
      }),
    close
  };
};
